import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { RequireRole } from '@/components/RequireRole';
import {
  EmptyState,
  ErrorPage,
  LoadingPage,
  SectionTitle,
  StatusBadge,
  Spinner,
  TestModeChip,
} from '@/components/primitives';
import { AlertFeedRow } from '@/components/alerts/AlertFeedRow';
import { describeItems, formatCount, hasItemCounts } from '@/lib/itemCounts';
import { STEP_KIND_ICON } from '@/lib/stepIcons';
import { KvRow } from '@/pages/WorkflowDetail';
import { keys, useAlerts, useChannels, useRun, useRunEvents } from '@/hooks/useSturmData';
import { runsApi } from '@/lib/apiClient';
import type { RunDetail, RunEvent, RunStepInstance } from '@/types/sturm';

const EVENT_FILTERS = [
  ['all', 'All'],
  ['problems', 'Problems'],
  ['step', 'Steps'],
  ['run', 'Run'],
] as const;

export function RunDetailPage() {
  const { slug = '', runId = '' } = useParams();
  const navigate = useNavigate();

  const { data: run, isLoading, isError, error } = useRun(runId);
  const isLive = run?.status === 'running';
  const { data: events = [] } = useRunEvents(runId, Boolean(isLive));
  const { data: alerts = [] } = useAlerts();
  const { data: channels = [] } = useChannels();

  const [filter, setFilter] = useState<(typeof EVENT_FILTERS)[number][0]>('all');
  const [openStep, setOpenStep] = useState<string | null>(null);

  const failedStep = run?.steps.find((s) => s.status === 'failed');

  useEffect(() => {
    if (failedStep && openStep === null) {
      setOpenStep(failedStep.id);
    }
  }, [failedStep, openStep]);

  if (isLoading) {
    return <LoadingPage label="Loading run…" />;
  }

  if (isError || !run) {
    return (
      <ErrorPage
        title="Could not load this run"
        message={error instanceof Error ? error.message : `No run exists with id "${runId}".`}
      />
    );
  }

  const shownEvents = events.filter((e) => matchesFilter(e, filter));
  const maxMs = Math.max(...run.steps.map((s) => s.durationMs), 1);
  const showItems = hasItemCounts(run.steps);
  const runAlerts = alerts.filter((a) => a.workflowSlug === run.workflowSlug).slice(0, 2);

  return (
    <div className="page">
      <div className="row gap-8 mb-16">
        <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/workflows/${slug}`)}>
          <Icon name="arrowLeft" size={13} /> All runs
        </button>
        <span className="tiny muted">·</span>
        <span className="tiny muted">{run.workflowName}</span>
      </div>

      <RunHeader run={run} isLive={Boolean(isLive)} failedStepName={failedStep?.name} />

      {run.errorType ? (
        <div className="card mb-20" style={{ borderColor: 'var(--error-soft)', overflow: 'hidden' }}>
          <div className="card-hd" style={{ background: 'var(--error-tint)', borderColor: 'var(--error-soft)' }}>
            <Icon name="alert" size={15} style={{ color: 'var(--error)' }} />
            <div className="flex1">
              <div className="card-hd-title" style={{ color: 'var(--error)' }}>
                {run.errorType}
                {failedStep ? ` in ${failedStep.name}` : ''}
              </div>
              <div className="card-hd-sub">
                {failedStep
                  ? `Failed on attempt ${failedStep.attempts} of ${failedStep.maxAttempts} — retry budget exhausted, run marked failed.`
                  : 'The run stopped with the error below.'}
              </div>
            </div>
          </div>
          <div className="card-pad">
            <div className="json">
              {run.errorMessage}
              {run.errorStackTrace ? `\n\n${run.errorStackTrace}` : ''}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid-2-1">
        <div className="stack gap-20">
          <div>
            <SectionTitle count={`${run.steps.filter((s) => s.status === 'completed').length}/${run.steps.length} completed`}>
              Steps
            </SectionTitle>
            {run.steps.length === 0 ? (
              <div className="card">
                <EmptyState title="No steps recorded yet" sub="The run has not reached its first step." />
              </div>
            ) : (
              <div className="lane">
                {run.steps.map((step) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    maxMs={maxMs}
                    showItems={showItems}
                    isOpen={openStep === step.id}
                    onToggle={() => setOpenStep(openStep === step.id ? null : step.id)}
                    runId={run.id}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-hd">
              <div className="flex1">
                <div className="card-hd-title">Event history</div>
                <div className="card-hd-sub">The durable log this run can be replayed from.</div>
              </div>
              <div className="seg">
                {EVENT_FILTERS.map(([id, label]) => (
                  <button
                    key={id}
                    className={`seg-btn ${filter === id ? 'is-active' : ''}`}
                    onClick={() => setFilter(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="evt-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 92 }}>At</th>
                    <th style={{ width: 190 }}>Event</th>
                    <th style={{ width: 170 }}>Step</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {shownEvents.map((event) => (
                    <tr key={event.sequence}>
                      <td>
                        <span className="mono tiny muted">{event.at}</span>
                      </td>
                      <td>
                        <span className={`evt-type lvl-${event.level}`}>{event.type}</span>
                      </td>
                      <td>
                        <span className="mono tiny muted">{event.step ?? '—'}</span>
                      </td>
                      <td>
                        <span
                          className="tiny"
                          style={{ color: event.level === 'err' ? 'var(--error)' : 'var(--ink-700)' }}
                        >
                          {event.detail}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shownEvents.length === 0 ? (
                <EmptyState title="No events match that filter" />
              ) : null}
            </div>
          </div>
        </div>

        <div className="stack gap-16">
          <div className="card">
            <div className="card-hd">
              <div className="card-hd-title">Run</div>
            </div>
            <div className="card-pad kv">
              <KvRow
                k="Workflow"
                v={
                  <span className="link-id" onClick={() => navigate(`/workflows/${run.workflowSlug}`)}>
                    {run.workflowSlug}
                  </span>
                }
              />
              <KvRow k="Version" v={run.version} />
              <KvRow k="Trigger" v={run.trigger} />
              <KvRow k="Mode" v={run.isTestMode ? 'Test, nothing written' : 'Live'} />
              <KvRow k="Queue" v={run.queue} />
              <KvRow k="Worker" v={run.worker ?? '—'} />
              <KvRow k="Started" v={run.started} />
              <KvRow k="Retention" v={run.retention} />
            </div>
          </div>

          <div className="card">
            <div className="card-hd">
              <div className="flex1">
                <div className="card-hd-title">Input</div>
              </div>
              <button
                className="btn btn-icon btn-sm btn-ghost"
                onClick={() => void navigator.clipboard.writeText(run.input ?? '')}
                title="Copy input"
              >
                <Icon name="copy" size={13} />
              </button>
            </div>
            <div className="card-pad" style={{ paddingTop: 0 }}>
              <div className="json">{formatJson(run.input)}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-hd">
              <Icon name="bell" size={14} />
              <div className="flex1">
                <div className="card-hd-title">Notifications</div>
              </div>
            </div>
            {runAlerts.length > 0 ? (
              <div>
                {runAlerts.map((alert) => (
                  <AlertFeedRow
                    key={alert.id}
                    alert={alert}
                    channels={channels}
                    onClick={() => navigate('/notifications')}
                  />
                ))}
              </div>
            ) : (
              <div className="card-pad">
                <div className="tiny muted">No rule fired for this run.</div>
                <button
                  className="btn btn-sm btn-secondary mt-12"
                  onClick={() => navigate(`/workflows/${run.workflowSlug}?tab=alerts`)}
                >
                  <Icon name="plus" size={12} /> Add a rule for {run.workflowSlug}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RunHeader({
  run,
  isLive,
  failedStepName,
}: {
  run: RunDetail;
  isLive: boolean;
  failedStepName?: string;
}) {
  const queryClient = useQueryClient();
  const elapsed = useElapsed(run.startedAt, isLive);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.run(run.id) });

  const terminate = useMutation({ mutationFn: () => runsApi.terminate(run.id), onSuccess: invalidate });
  const retry = useMutation({ mutationFn: () => runsApi.retry(run.id), onSuccess: invalidate });
  const rerun = useMutation({ mutationFn: () => runsApi.rerun(run.id), onSuccess: invalidate });

  const tone =
    run.status === 'failed'
      ? { background: 'var(--error-soft)', color: 'var(--error)' }
      : run.status === 'running'
        ? { background: 'var(--accent-tint)', color: 'var(--accent)' }
        : { background: 'var(--ink-100)', color: 'var(--ink-600)' };

  return (
    <div className="run-hd">
      <div className="tile lg" style={{ ...tone, borderColor: 'transparent' }}>
        <Icon
          name={run.status === 'failed' ? 'alert' : run.status === 'running' ? 'refresh' : 'check'}
          size={20}
          className={isLive ? 'spin' : ''}
        />
      </div>
      <div className="flex1" style={{ minWidth: 0 }}>
        <div className="row gap-10">
          <span className="run-id">{run.runKey}</span>
          <StatusBadge status={run.status} />
          <span className="wf-kind">{run.trigger}</span>
          {run.isTestMode ? <TestModeChip /> : null}
        </div>
        <div className="tiny muted mt-4">
          started {run.started}
          {run.businessKey ? (
            <>
              {' · key '}
              <span className="mono">{run.businessKey}</span>
            </>
          ) : null}
          {' · '}
          {run.queue}
          {run.worker ? ` · ${run.worker}` : ''}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="stat-label" style={{ justifyContent: 'flex-end' }}>
          {isLive ? 'Elapsed' : 'Duration'}
        </div>
        <div className={isLive ? 'run-elapsed' : 'run-id'}>{isLive ? elapsed : run.duration}</div>
      </div>
      <RequireRole min="manager">
        <div className="row gap-6" style={{ marginLeft: 12 }}>
          {isLive ? (
            <button
              className="btn btn-danger"
              onClick={() => terminate.mutate()}
              disabled={terminate.isPending}
            >
              <Icon name="stop" size={12} /> Terminate
            </button>
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => void navigator.clipboard.writeText(run.input ?? '')}
              >
                <Icon name="copy" size={13} /> Copy input
              </button>
              <button
                className="btn btn-accent"
                onClick={() => (failedStepName ? retry.mutate() : rerun.mutate())}
                disabled={retry.isPending || rerun.isPending}
              >
                <Icon name="refresh" size={13} />
                {failedStepName ? ` Retry from ${failedStepName}` : ' Run again'}
              </button>
            </>
          )}
        </div>
      </RequireRole>
    </div>
  );
}

function StepRow({
  step,
  maxMs,
  showItems,
  isOpen,
  onToggle,
  runId,
}: {
  step: RunStepInstance;
  maxMs: number;
  showItems: boolean;
  isOpen: boolean;
  onToggle: () => void;
  runId: string;
}) {
  const queryClient = useQueryClient();
  const retryStep = useMutation({
    mutationFn: () => runsApi.retryStep(runId, step.name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.run(runId) }),
  });

  const idle = step.status === 'waiting' || step.status === 'skipped';
  const width = maxMs > 0 ? (step.durationMs / maxMs) * 100 : 0;
  const itemsFailed = (step.failedItems ?? 0) > 0;

  return (
    <div className="lane-row">
      <div className="lane-rail">
        <div className={`lane-node st-${step.status}`}>{nodeIcon(step.status)}</div>
      </div>
      <div className="lane-body">
        <div className={`lane-card st-${step.status} ${isOpen ? 'is-open' : ''}`} onClick={onToggle}>
          <div style={{ minWidth: 0, width: 220 }}>
            <div className="row gap-8">
              <span className="lane-name">{step.name}</span>
              {step.instances > 1 ? <span className="badge badge-neutral">×{step.instances}</span> : null}
            </div>
            <div className="tiny muted mt-4 row gap-8">
              <span className="row gap-4">
                <Icon name={STEP_KIND_ICON[step.kind]} size={11} />
                {step.kind}
              </span>
              {step.attempts > 1 ? (
                <span style={{ color: 'var(--warn)' }}>{step.attempts} attempts</span>
              ) : null}
              {idle ? <span className="st-muted">{step.status}</span> : null}
            </div>
          </div>
          <div className="dbar">
            {step.attempts > 1
              ? Array.from({ length: step.attempts - 1 }).map((_, i) => (
                  <i key={i} className="d-retry" style={{ width: `${width * 0.22}%` }} />
                ))
              : null}
            <i className={`d-${idle ? 'none' : step.status}`} style={{ width: `${width}%` }} />
          </div>
          <div className="lane-meta">
            {showItems ? (
              <div className="lane-stat" title={describeItems(step.items, step.failedItems)}>
                <span className="lane-stat-l">Items</span>
                <span className={`lane-stat-v ${itemsFailed ? 'bad' : ''}`}>{formatCount(step.items)}</span>
              </div>
            ) : null}
            <div className="lane-stat">
              <span className="lane-stat-l">Took</span>
              <span className="lane-stat-v">{step.duration}</span>
            </div>
            <div className="lane-stat">
              <span className="lane-stat-l">Attempts</span>
              <span className={`lane-stat-v ${step.attempts > 1 ? 'bad' : ''}`}>
                {idle ? '—' : step.attempts}
              </span>
            </div>
            <Icon name={isOpen ? 'chevronDown' : 'chevron'} size={14} style={{ color: 'var(--ink-300)' }} />
          </div>
        </div>
        {isOpen ? (
          <div className="lane-detail">
            <div className="kv">
              <KvRow k="Worker" v={step.worker ?? '—'} />
              <KvRow k="Timeout" v={step.timeout} />
              <KvRow
                k="Attempts"
                v={step.status === 'waiting' ? 'not started' : `${step.attempts} of ${step.maxAttempts}`}
              />
              {step.items != null ? (
                <KvRow
                  k="Items"
                  v={
                    <span style={itemsFailed ? { color: 'var(--error)' } : undefined}>
                      {describeItems(step.items, step.failedItems)}
                    </span>
                  }
                />
              ) : null}
              {step.errorType ? (
                <KvRow k="Error" v={<span style={{ color: 'var(--error)' }}>{step.errorType}</span>} />
              ) : null}
            </div>
            <RequireRole min="manager">
              <div className="row gap-6 mt-12">
                <button
                  className="btn btn-xs btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    retryStep.mutate();
                  }}
                  disabled={retryStep.isPending}
                >
                  <Icon name="refresh" size={11} /> Retry this step
                </button>
              </div>
            </RequireRole>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function nodeIcon(status: RunStepInstance['status']) {
  switch (status) {
    case 'completed':
      return <Icon name="check" size={11} stroke={2.5} />;
    case 'running':
      return <Spinner size={11} />;
    case 'failed':
      return <Icon name="close" size={11} stroke={2.5} />;
    case 'timedout':
      return <Icon name="clock" size={11} />;
    default:
      return <Icon name="minus" size={11} />;
  }
}

function matchesFilter(event: RunEvent, filter: string): boolean {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'problems') {
    return event.level === 'err' || event.level === 'warn';
  }
  return event.type.startsWith(filter);
}

function formatJson(raw: string | null): string {
  if (!raw) {
    return '{}';
  }

  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// The clock ticks locally from an absolute start time; the server never pushes per-second updates.
function useElapsed(startedAt: string | null, isLive: boolean): string {
  const start = useMemo(() => (startedAt ? new Date(startedAt).getTime() : null), [startedAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isLive) {
      return;
    }

    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [isLive]);

  if (!start) {
    return '—';
  }

  const total = Math.max(0, Math.floor((now - start) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return hours > 0 ? `${hours}h ${pad(minutes)}m ${pad(seconds)}s` : `${minutes}m ${pad(seconds)}s`;
}
